// src/Pages/CreateRFQ.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import '../Style/CreateRFQ.css';
import { FaCloudUploadAlt, FaSpinner, FaTimes } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { auth, db, storage } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/* ───────── utilities ───────── */
const debounce = (fn, wait) => {
  let t;
  return (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), wait);
  };
};

const fileKind = (name, type) => {
  if (/pdf$/i.test(name) || type === 'application/pdf') return 'pdf';
  if (/csv$/i.test(name) || type === 'text/csv') return 'csv';
  if (/image\//.test(type)) return 'image';
  return 'other';
};

const MAX_FILES = 10;
const MAX_SIZE_MB = 10;
const RFQ_STATUSES = ['Order pending', 'Order received'];

/* ───────── component ───────── */
export default function CreateRFQ() {
  /* base state */
  const [rfqNo, setRfqNo] = useState('Loading…'); // now editable
  const [customer, setCustomer] = useState('');
  const [customers, setCustomers] = useState([]);

  const [status, setStatus] = useState(RFQ_STATUSES[0]);
  const [rfqDate, setRfqDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [expiryDate, setExpiryDate] = useState('');
  const [customerRef, setCustomerRef] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [deliveryTerms, setDeliveryTerms] = useState('');
  const [notes, setNotes] = useState('');

  /* files */
  const [files, setFiles] = useState([]);
  const [fileMeta, setFileMeta] = useState([]);
  const [dragActive, setDrag] = useState(false);

  /* ui flags */
  const [showModal, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  /* validation state */
  const [errors, setErrors] = useState({
    customer: false,
    rfqNo: false
  });

  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  /* ───────── initial data ───────── */
  useEffect(() => {
    const load = async user => {
      if (!user) {
        toast.error('Please login to create an RFQ.', { className: 'toast-error' });
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        // determine next RFQ number (scoped to this user)
        const q = query(collection(db, 'RFQs'), where('authId', '==', user.uid));
        const snap = await getDocs(q);
        setRfqNo(`RFQ${snap.size + 1}`);

        // load customers list
        const custSnap = await getDocs(collection(db, 'Customers'));
        setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch prerequisite data.', { className: 'toast-error' });
      } finally {
        setLoading(false);
      }
    };
    const unsub = auth.onAuthStateChanged(load);
    return () => unsub();
  }, []);

  /* ───────── debounced large text input ───────── */
  const handleNotesInput = useCallback(debounce(v => setNotes(v), 300), []);

  /* ───────── validation ───────── */
  const validate = useCallback(() => {
    const newErrors = {
      customer: !customer,
      rfqNo: !rfqNo.trim()
    };
    setErrors(newErrors);
    return !newErrors.customer && !newErrors.rfqNo;
  }, [customer, rfqNo]);

  /* ───────── file uploads ───────── */
  const addFiles = newFiles => {
    if (!newFiles.length) return;
    let existing = [...files];
    let meta = [...fileMeta];

    for (const f of newFiles) {
      if (existing.length >= MAX_FILES) {
        toast.error(`Max ${MAX_FILES} files reached.`, { className: 'toast-error' });
        break;
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`${f.name} exceeds ${MAX_SIZE_MB} MB.`, { className: 'toast-error' });
        continue;
      }
      const kind = fileKind(f.name, f.type);
      existing.push(f);
      meta.push({
        name: f.name,
        kind,
        preview: kind === 'image' ? URL.createObjectURL(f) : null
      });
    }
    setFiles(existing);
    setFileMeta(meta);
  };

  const handleInputFiles = e => addFiles(Array.from(e.target.files || []));
  const onDragOver = e => { e.preventDefault(); setDrag(true); };
  const onDragLeave = () => setDrag(false);
  const onDrop = e => {
    e.preventDefault();
    setDrag(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };
  const openPicker = () => fileInputRef.current?.click();

  const removeFile = idx => {
    setFiles(f => f.filter((_, i) => i !== idx));
    setFileMeta(m => {
      const target = m[idx];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return m.filter((_, i) => i !== idx);
    });
  };

  useEffect(() => () => {
    // cleanup previews on unmount
    fileMeta.forEach(m => m.preview && URL.revokeObjectURL(m.preview));
  }, [fileMeta]);

  const uploadAll = async () => {
    if (!files.length) return { fileMeta: [], fileUrls: [] };
    try {
      const uploaded = await Promise.all(
        files.map(async f => {
          const storageRef = ref(storage, `RFQs/${rfqNo}/${Date.now()}_${f.name}`);
          await uploadBytes(storageRef, f);
          const url = await getDownloadURL(storageRef);
          return { name: f.name, url, kind: fileKind(f.name, f.type) };
        })
      );
      return { fileMeta: uploaded, fileUrls: uploaded.map(u => u.url) };
    } catch (err) {
      toast.error('File upload failed.', { className: 'toast-error' });
      throw err;
    }
  };

  /* ───────── save RFQ ───────── */
  const saveRFQ = async e => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please correct highlighted fields.', { className: 'toast-error' });
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      toast.error('Please login first.', { className: 'toast-error' });
      return;
    }
    try {
      setSaving(true);

      // ===== duplicate RFQ‑No guard =====
      const dupQ = query(collection(db, 'RFQs'), where('rfqNo', '==', rfqNo.trim()));
      const dupSnap = await getDocs(dupQ);
      if (!dupSnap.empty) {
        toast.error(`RFQ number "${rfqNo}" already exists. Pick a unique number.`, {
          className: 'toast-error'
        });
        setErrors(prev => ({ ...prev, rfqNo: true }));
        setSaving(false);
        return;
      }

      const uSnap = await getDoc(doc(db, 'users', user.uid));
      const { firstName = '', lastName = '' } = uSnap.exists() ? uSnap.data() : {};

      const uploaded = await uploadAll();

      await addDoc(collection(db, 'RFQs'), {
        rfqNo: rfqNo.trim(),
        customer,
        status,
        rfqDate,
        expiryDate,
        customerRef: customerRef.trim(),
        paymentTerms: paymentTerms.trim(),
        deliveryTerms: deliveryTerms.trim(),
        notes: notes.trim(),
        fileMeta: uploaded.fileMeta,
        fileUrls: uploaded.fileUrls,
        authId: user.uid,
        createdBy: `${firstName} ${lastName}`.trim(),
        createdAt: serverTimestamp()
      });

      toast.success('RFQ saved successfully!', { className: 'toast-success' });
      setShow(true);
      clearForm();
      // auto‑suggest next number
      setRfqNo(prev => {
        const n = parseInt(prev.replace(/\D/g, '') || '0', 10) + 1;
        return `RFQ${n}`;
      });
    } catch (err) {
      console.error(err);
      toast.error('Could not save RFQ.', { className: 'toast-error' });
    } finally {
      setSaving(false);
    }
  };

  /* ───────── clear form ───────── */
  const clearForm = () => {
    setCustomer('');
    setStatus(RFQ_STATUSES[0]);
    setRfqDate(new Date().toISOString().slice(0, 10));
    setExpiryDate('');
    setCustomerRef('');
    setPaymentTerms('');
    setDeliveryTerms('');
    setNotes('');
    fileMeta.forEach(m => m.preview && URL.revokeObjectURL(m.preview));
    setFiles([]);
    setFileMeta([]);
    setErrors({ customer: false, rfqNo: false });
  };

  /* modal handlers */
  const closeM = () => setShow(false);
  const goAll = () => navigate('/Jobs');
  const goCreateJob = () => navigate('/CreateJob');

  /* ESC closes success modal */
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') setShow(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  /* ───────── render ───────── */
  return (
    <div className="createrfq-page">
      <ToastContainer position="top-right" autoClose={2500} />

      <Sidebar />

      <main className="createrfq-main">
        <div className="title-row">
          <h2>Create RFQ</h2>
          <span className="badge-id">{rfqNo}</span>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <FaSpinner className="spin" size={54} />
            <p>Loading prerequisite data…</p>
          </div>
        ) : (
          <form className="createrfq-form" onSubmit={saveRFQ}>
            {/* ───────── RFQ header ───────── */}
            <section className="card">
              <h3>RFQ Details</h3>
              <div className="grid-4">
                {/* Editable RFQ No */}
                <div className="field">
                  <label>RFQ No <span className="required">*</span></label>
                  <input
                    value={rfqNo}
                    onChange={e => setRfqNo(e.target.value)}
                    className={errors.rfqNo ? 'input-error' : ''}
                  />
                  {errors.rfqNo && <span className="error-text">Required &amp; unique</span>}
                </div>

                {/* Customer */}
                <div className="field">
                  <label>Customer <span className="required">*</span></label>
                  <select
                    value={customer}
                    onChange={e => setCustomer(e.target.value)}
                    className={errors.customer ? 'input-error' : ''}
                  >
                    <option value="">Select customer</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                  {errors.customer && <span className="error-text">Required</span>}
                </div>

                {/* Status */}
                <div className="field">
                  <label>Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)}>
                    {RFQ_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>

                {/* Customer Reference */}
                <div className="field">
                  <label>Customer Reference</label>
                  <input
                    placeholder="PO number / ref"
                    value={customerRef}
                    onChange={e => setCustomerRef(e.target.value)}
                  />
                </div>
              </div>

              {/* Dates & terms */}
              <div className="grid-4 mt">
                <div className="field">
                  <label>RFQ Date</label>
                  <input
                    type="date"
                    value={rfqDate}
                    onChange={e => setRfqDate(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    value={expiryDate}
                    onChange={e => setExpiryDate(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>Payment Terms</label>
                  <input
                    placeholder="e.g. 30 days"
                    value={paymentTerms}
                    onChange={e => setPaymentTerms(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>Delivery Terms</label>
                  <input
                    placeholder="e.g. FOB, Ex Works..."
                    value={deliveryTerms}
                    onChange={e => setDeliveryTerms(e.target.value)}
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="field mt">
                <label>Notes / Additional Info</label>
                <textarea
                  rows={3}
                  defaultValue={notes}
                  placeholder="Anything else relevant to this RFQ"
                  onChange={e => handleNotesInput(e.target.value)}
                />
              </div>
            </section>

            {/* ───────── file uploads ───────── */}
            <section className="card">
              <h3>Attachments</h3>
              <p className="hint">
                Upload images / PDF / CSV (max {MAX_FILES} files, {MAX_SIZE_MB} MB each).
              </p>
              <div
                className={`upload-area ${dragActive ? 'drag' : ''}`}
                onClick={openPicker}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                role="button"
                tabIndex={0}
              >
                <FaCloudUploadAlt size={34} />
                <p>{dragActive ? 'Drop to upload' : 'Click or drag files here'}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf,text/csv,.csv"
                  multiple
                  hidden
                  onChange={handleInputFiles}
                />
              </div>

              {fileMeta.length > 0 && (
                <div className="files-grid">
                  {fileMeta.map((f, i) => (
                    <div key={i} className="file-chip">
                      <div className={`preview ${f.kind}`}>
                        {f.kind === 'image' ? (
                          <img src={f.preview} alt={f.name} />
                        ) : (
                          <span className="badge-kind">{f.kind.toUpperCase()}</span>
                        )}
                      </div>
                      <div className="fname" title={f.name}>{f.name}</div>
                      <button
                        type="button"
                        className="remove-chip"
                        onClick={() => removeFile(i)}
                        aria-label={`Remove ${f.name}`}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ───────── actions ───────── */}
            <section className="card action-card">
              <div className="actions-row">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save RFQ'}
                </button>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={clearForm}
                  disabled={saving}
                >
                  Clear Form
                </button>
              </div>
            </section>
          </form>
        )}
      </main>

      {/* ───────── saving overlay ───────── */}
      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={58} />
          <p>Saving…</p>
        </div>
      )}

      {/* ───────── success modal ───────── */}
      {showModal && (
        <div className="modal-overlay" role="dialog" aria-labelledby="modal-title">
          <div className="modal-card">
            <button
              className="modal-close"
              onClick={closeM}
              aria-label="Close modal"
            >
              ×
            </button>
            <h3 id="modal-title">RFQ Created</h3>
            <p>Select an action below:</p>
            <div className="modal-actions">
              <button onClick={closeM}>Create Another RFQ</button>
              <button onClick={goAll}>View All RFQs</button>
              <button onClick={goCreateJob}>Create Job</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
